import { FlowObsoleteBlockType } from 'flow-schema';

import { advancedFhirBlockConfig } from './Nodes/Configs/AdvancedFhir/config.ts';
import { asynchronousJobNodeConfig } from './Nodes/Configs/AsynchronousJobs/config.ts';
import { authorizationSubmissionConfig } from './Nodes/Configs/AuthorizationSubmission/config.ts';
import { callbackBlockConfig } from './Nodes/Configs/Callback/config.ts';
import { careGapAlgorithmNodeConfig } from './Nodes/Configs/CareGap/config';
import { conditionalConfig } from './Nodes/Configs/Conditional/config.ts';
import { conversationNodeConfig } from './Nodes/Configs/Conversation/config.js';
import { createEncounterSummaryConfig } from './Nodes/Configs/CreateEncounterSummary/Node.tsx';
import { createNotableObjectConfig } from './Nodes/Configs/CreateNotableObject/config.ts';
import { createPatientOrderConfig } from './Nodes/Configs/CreatePatientOrder/config.tsx';
import { documentExtractConfig } from './Nodes/Configs/DocumentExtract/config.tsx';
import { documentSearchNodeConfig } from './Nodes/Configs/DocumentSearch/config.tsx';
import { DocumentTemplateConfig } from './Nodes/Configs/DocumentTemplate/config.ts';
import { evaluationNodeConfig } from './Nodes/Configs/Evaluation/config.tsx';
import { exportExternalDocumentPacketConfig } from './Nodes/Configs/ExportExternalDocumentPacket/config.ts';
import { exportTextToBucketConfig } from './Nodes/Configs/ExportToBucket/config.ts';
import { exportToSftpConfig } from './Nodes/Configs/ExportToSftp/config.ts';
import { faxConfig } from './Nodes/Configs/Fax/config.ts';
import { fhirBlockConfig } from './Nodes/Configs/Fhir/FhirNode.tsx';
import { fileConversionBlockConfig } from './Nodes/Configs/FileConversion/config.js';
import { filterListConfig } from './Nodes/Configs/FilterList/config.tsx';
import { formConfig } from './Nodes/Configs/Form/config.ts';
import { formV2Config } from './Nodes/Configs/FormV2/config.ts';
import { GenerateFileConfig } from './Nodes/Configs/GenerateFile/config.ts';
import { getExternalDocumentPacketConfig } from './Nodes/Configs/GetExternalDocumentPacket/config.ts';
import { getFormSetNodeConfig } from './Nodes/Configs/GetFormSet/config.js';
import { getInsurancesConfig } from './Nodes/Configs/GetInsurances/config.ts';
import { getNotableObjectConfig } from './Nodes/Configs/GetNotableObject/config.tsx';
import { getPatientConfig } from './Nodes/Configs/GetPatient/Node.tsx';
import { getPatientOrderConfig } from './Nodes/Configs/GetPatientOrder/Node.tsx';
import { getProviderConfig } from './Nodes/Configs/GetProvider/config.ts';
import { getSftpFileConfig } from './Nodes/Configs/GetSftpFile/config.tsx';
import { hccAlgorithmNodeConfig } from './Nodes/Configs/HccAlgorithm/config.ts';
import { hieGetDocumentsConfig } from './Nodes/Configs/HieGetDocuments/config.js';
import { hieQueryDocumentsConfig } from './Nodes/Configs/HieQueryDocuments/Node.tsx';
import { hl7BlockConfig } from './Nodes/Configs/Hl7/config.tsx';
import { humanTaskConfig } from './Nodes/Configs/HumanTask/config.tsx';
import { informationExtractionNodeConfig } from './Nodes/Configs/InformationExtraction/config.tsx';
import { insuranceCardNodeConfig } from './Nodes/Configs/InsuranceCard/config.ts';
import { iteratorV1Config } from './Nodes/Configs/Iterator/v1/config.tsx';
import { iteratorV2Config } from './Nodes/Configs/Iterator/v2/config.tsx';
import { llmNodeConfig } from './Nodes/Configs/Llm/config.tsx';
import { medicalNecessityCheckConfig } from './Nodes/Configs/MedicalNecessityCheck/config.js';
import { mirthBlockConfig } from './Nodes/Configs/Mirth/config.tsx';
import { montageOnBaseConfig } from './Nodes/Configs/MontageOnBase/config.tsx';
import { obsoleteNodeConfig } from './Nodes/Configs/Obsolete/config.tsx';
import { ocrConfig } from './Nodes/Configs/Ocr/v1/Node.tsx';
import { ocrV2Config } from './Nodes/Configs/Ocr/v2/config.ts';
import { outputConfig } from './Nodes/Configs/Output/config.ts';
import { PatientDataStoreConfig } from './Nodes/Configs/PatientDataStore/config.ts';
import { realTimeEligibilityConfig } from './Nodes/Configs/RealTimeEligibility/config.tsx';
import { repeatConfig } from './Nodes/Configs/Repeat/config.ts';
import { restBlockConfig } from './Nodes/Configs/Rest/config.ts';
import { restApiClassicAuthorizationConfig } from './Nodes/Configs/RestApiClassicAuthorization/config.ts';
import { schedulingConfig } from './Nodes/Configs/Scheduling/config';
import { scriptedFlowConfig } from './Nodes/Configs/ScriptedFlow/config.ts';
import { sendEmailConfig } from './Nodes/Configs/SendEmail/config.ts';
import { sendSmsNodeConfig } from './Nodes/Configs/SendSms/config.tsx';
import { serviceReviewConfig } from './Nodes/Configs/ServiceReview/config.ts';
import { skillV2Config } from './Nodes/Configs/Skill/v2/config.ts';
import { startConfig } from './Nodes/Configs/Start/config.ts';
import { subFlowV1Config } from './Nodes/Configs/SubFlow/v1/config.tsx';
import { subFlowV2Config } from './Nodes/Configs/SubFlow/v2/config.tsx';
import { switchConfig } from './Nodes/Configs/Switch/config.ts';
import { textBlockConfig } from './Nodes/Configs/Text/config.ts';
import { updateFlowConfigConfig } from './Nodes/Configs/UpdateFlowConfig/config.ts';
import { updateNotableObjectConfig } from './Nodes/Configs/UpdateNotableObject/config.ts';
import { voiceCallNodeConfig, voiceCallV2NodeConfig } from './Nodes/Configs/VoiceCall/config.tsx';
import { voiceTranscribeNodeConfig } from './Nodes/Configs/VoiceTranscribe/config.tsx';
import { waitConfig } from './Nodes/Configs/Wait/v1/WaitNode.tsx';
import { waitConfigV2 } from './Nodes/Configs/Wait/v2/config.ts';
import { waitForFormCompletionConfig } from './Nodes/Configs/WaitForFormCompletion/config.tsx';
import { waitForSignalConfig } from './Nodes/Configs/WaitForSignal/Node.tsx';
import { type BlockConfigProps } from './Nodes/types.ts';
import { BlockType, type UiBlockType, UnknownBlockType } from './types.ts';

export const blockConfigsByBlockType: Record<UiBlockType, BlockConfigProps<any>> = {
  // Start block types
  [BlockType.Start]: startConfig,
  // Special block types not pickable by users
  [BlockType.SkillV2]: skillV2Config,
  [UnknownBlockType.Unknown]: obsoleteNodeConfig,
  // Obsolete Blocks
  [FlowObsoleteBlockType.ObsoleteHumanReview]: obsoleteNodeConfig,
  [FlowObsoleteBlockType.ObsoleteApiSubmissions]: obsoleteNodeConfig,
  [FlowObsoleteBlockType.ObsoleteGetEncounterSummary]: obsoleteNodeConfig,
  [FlowObsoleteBlockType.ObsoleteSkill]: obsoleteNodeConfig,
  [FlowObsoleteBlockType.ObsoleteSendToLabelboxForReview]: obsoleteNodeConfig,
  [FlowObsoleteBlockType.ObsoleteWaitForAvailableSchedulingSlot]: obsoleteNodeConfig,
  // Block types that users can add
  [BlockType.AdvancedFhir]: advancedFhirBlockConfig,
  [BlockType.CareGapAlgorithmBlock]: careGapAlgorithmNodeConfig,
  [BlockType.Conversation]: conversationNodeConfig,
  [BlockType.CreateEncounterSummary]: createEncounterSummaryConfig,
  [BlockType.CreatePatientOrder]: createPatientOrderConfig,
  [BlockType.DocumentExtract]: documentExtractConfig,
  [BlockType.DocumentTemplate]: DocumentTemplateConfig,
  [BlockType.GenerateFile]: GenerateFileConfig,
  [BlockType.Fax]: faxConfig,
  [BlockType.Fhir]: fhirBlockConfig,
  [BlockType.Form]: formConfig,
  [BlockType.FormV2]: formV2Config,
  [BlockType.GetExternalDocumentPacket]: getExternalDocumentPacketConfig,
  [BlockType.GetFormSet]: getFormSetNodeConfig,
  [BlockType.GetInsurances]: getInsurancesConfig,
  [BlockType.GetPatient]: getPatientConfig,
  [BlockType.GetPatientOrder]: getPatientOrderConfig,
  [BlockType.GetNotableObject]: getNotableObjectConfig,
  [BlockType.GetProvider]: getProviderConfig,
  [BlockType.HCCAlgorithmBlock]: hccAlgorithmNodeConfig,
  [BlockType.LlmPrompt]: llmNodeConfig,
  [BlockType.Ocr]: ocrConfig,
  [BlockType.OcrV2]: ocrV2Config,
  [BlockType.MedicalNecessityCheck]: medicalNecessityCheckConfig,
  [BlockType.PatientDataStore]: PatientDataStoreConfig,
  [BlockType.Rest]: restBlockConfig,
  [BlockType.ScriptedFlow]: scriptedFlowConfig,
  [BlockType.SubFlowV1]: subFlowV1Config,
  [BlockType.SubFlowV2]: subFlowV2Config,
  [BlockType.Switch]: switchConfig,
  [BlockType.Wait]: waitConfig,
  [BlockType.WaitForSignal]: waitForSignalConfig,
  [BlockType.WaitForFormCompletion]: waitForFormCompletionConfig,
  [BlockType.WaitV2]: waitConfigV2,
  [BlockType.AsynchronousExportJob]: asynchronousJobNodeConfig,
  [BlockType.AsynchronousExtractJob]: asynchronousJobNodeConfig, // Deprecated - TODO - REMOVE
  [BlockType.Callback]: callbackBlockConfig,
  [BlockType.Evaluation]: evaluationNodeConfig,
  [BlockType.ExportTextToBucket]: exportTextToBucketConfig,
  [BlockType.ExportExternalDocumentPacket]: exportExternalDocumentPacketConfig,
  [BlockType.ExportToSftp]: exportToSftpConfig,
  [BlockType.FilterList]: filterListConfig,
  [BlockType.HumanTask]: humanTaskConfig,
  [BlockType.InsuranceCard]: insuranceCardNodeConfig,
  [BlockType.IteratorV1]: iteratorV1Config,
  [BlockType.IteratorV2]: iteratorV2Config,
  [BlockType.Repeat]: repeatConfig,
  [BlockType.Scheduling]: schedulingConfig,
  [BlockType.SendEmail]: sendEmailConfig,
  [BlockType.SendSms]: sendSmsNodeConfig,
  [BlockType.VoiceCall]: voiceCallNodeConfig,
  [BlockType.VoiceCallV2]: voiceCallV2NodeConfig,
  [BlockType.VoiceTranscribe]: voiceTranscribeNodeConfig,
  [BlockType.AuthorizationSubmission]: authorizationSubmissionConfig,
  [BlockType.InformationExtraction]: informationExtractionNodeConfig,
  [BlockType.UpdateFlowConfig]: updateFlowConfigConfig,
  [BlockType.UpdateNotableObject]: updateNotableObjectConfig,
  [BlockType.CreateNotableObject]: createNotableObjectConfig,
  [BlockType.Hl7]: hl7BlockConfig,
  [BlockType.HieQueryDocuments]: hieQueryDocumentsConfig,
  [BlockType.HieGetDocuments]: hieGetDocumentsConfig,
  [BlockType.DocumentSearch]: documentSearchNodeConfig,
  [BlockType.Output]: outputConfig,
  [BlockType.RestApiClassicAuthorization]: restApiClassicAuthorizationConfig,
  [BlockType.GetSftpFile]: getSftpFileConfig,
  [BlockType.RealTimeEligibility]: realTimeEligibilityConfig,
  [BlockType.FileConversion]: fileConversionBlockConfig,
  [BlockType.Mirth]: mirthBlockConfig,
  [BlockType.MontageOnBase]: montageOnBaseConfig,
  [BlockType.Text]: textBlockConfig,
  [BlockType.ServiceReview]: serviceReviewConfig,
  [BlockType.Conditional]: conditionalConfig,
};
